/// <reference types="@standard-schema/spec" />

import {defineRegleConfig} from '@regle/core';
import {withMessage} from '@regle/rules';
import * as regleRules from '@regle/rules';
import {useFormat, useTranslation} from '#imports';
import {bic, maxPrice, minPrice, maxYear, minYear, maxImageDimensions, minImageDimensions, iban} from './custom-rules';

export const defaultRegleConfig = defineRegleConfig({
    rules: () => {
        const {t} = useTranslation();
        const {formatDate, formatNumber, formatFileSize} = useFormat();

        return {
            // - Regle built-in rules
            alpha: withMessage(regleRules.alpha, ({$params: [options]}) => {
                if (options?.allowSymbols) {
                    return t('generic.forms.validation.errors.alpha.allowSymbols');
                }
                return t('generic.forms.validation.errors.alpha');
            }),
            alphaNum: withMessage(regleRules.alphaNum, ({$params: [options]}) => {
                if (options?.allowSymbols) {
                    return t('generic.forms.validation.errors.alphaNum.allowSymbols');
                }
                return t('generic.forms.validation.errors.alphaNum');
            }),
            between: withMessage(regleRules.between, ({$params: [min, max]}) => t('generic.forms.validation.errors.between', [min, max])),
            checked: withMessage(regleRules.checked, t('generic.forms.validation.errors.checked')),
            contains: withMessage(regleRules.contains, ({$params: [substring]}) => t('generic.forms.validation.errors.contains', [substring])),
            containsSpecialCharacter: withMessage(regleRules.containsSpecialCharacter, ({minCharactersCount}) =>
                t('generic.forms.validation.errors.containsSpecialCharacter', {count: minCharactersCount}),
            ),
            containsUppercase: withMessage(regleRules.containsUppercase, ({minUppercaseCount}) =>
                t('generic.forms.validation.errors.containsUppercase', {count: minUppercaseCount}),
            ),
            date: withMessage(regleRules.date, t('generic.forms.validation.errors.date')),
            dateAfter: withMessage(regleRules.dateAfter, ({$params: [maxDate, options]}) =>
                (options?.allowEqual ?? true)
                    ? t('generic.forms.validation.errors.dateAfter.equal', [formatDate(maxDate, {dateStyle: 'short'})])
                    : t('generic.forms.validation.errors.dateAfter', [formatDate(maxDate, {dateStyle: 'short'})]),
            ),
            dateBefore: withMessage(regleRules.dateBefore, ({$params: [minDate, options]}) =>
                (options?.allowEqual ?? true)
                    ? t('generic.forms.validation.errors.dateBefore.equal', [formatDate(minDate, {dateStyle: 'short'})])
                    : t('generic.forms.validation.errors.dateBefore', [formatDate(minDate, {dateStyle: 'short'})]),
            ),
            dateBetween: withMessage(regleRules.dateBetween, ({$params: [startDate, endDate]}) =>
                t('generic.forms.validation.errors.dateBetween', [
                    formatDate(startDate, {dateStyle: 'short'}),
                    formatDate(endDate, {dateStyle: 'short'}),
                ]),
            ),
            decimal: withMessage(regleRules.decimal, t('generic.forms.validation.errors.decimal')),
            email: withMessage(regleRules.email, t('generic.forms.validation.errors.email')),
            endsWith: withMessage(regleRules.endsWith, ({$params: [suffix]}) => t('generic.forms.validation.errors.endsWith', [suffix])),
            exactDigits: withMessage(regleRules.exactDigits, ({$params: [count]}) => t('generic.forms.validation.errors.exactDigits', [count])),
            exactLength: withMessage(regleRules.exactLength, ({$params: [count]}) => t('generic.forms.validation.errors.exactLength', [count])),
            exactValue: withMessage(regleRules.exactValue, ({$params: [value]}) => t('generic.forms.validation.errors.exactValue', [value])),
            file: withMessage(regleRules.file, t('generic.forms.validation.errors.file')),
            fileType: withMessage(regleRules.fileType, ({$params: [types]}) => t('generic.forms.validation.errors.fileType', [types.join(', ')])),
            httpUrl: withMessage(regleRules.httpUrl, t('generic.forms.validation.errors.httpUrl')),
            integer: withMessage(regleRules.integer, t('generic.forms.validation.errors.integer')),
            maxFileSize: withMessage(regleRules.maxFileSize, ({$params: [size]}) => t('generic.forms.validation.errors.maxFileSize', [size])),
            maxLength: withMessage(regleRules.maxLength, ({$value, $params: [maxCount, options]}) => {
                const allowEqual = options?.allowEqual ?? true;
                if (Array.isArray($value)) {
                    if (allowEqual) {
                        return t('generic.forms.validation.errors.maxLength.array.equal', [maxCount]);
                    } else {
                        return t('generic.forms.validation.errors.maxLength.array', [maxCount]);
                    }
                } else if (allowEqual) {
                    return t('generic.forms.validation.errors.maxLength.equal', [maxCount]);
                } else {
                    return t('generic.forms.validation.errors.maxLength', [maxCount]);
                }
            }),
            maxValue: withMessage(regleRules.maxValue, ({$params: [maxCount, options]}) => {
                const formatedMaxCount = formatNumber(maxCount);
                if (options?.allowEqual ?? true) {
                    return t('generic.forms.validation.errors.maxValue.equal', [formatedMaxCount]);
                } else {
                    return t('generic.forms.validation.errors.maxValue', [formatedMaxCount]);
                }
            }),
            minFileSize: withMessage(regleRules.minFileSize, ({$params: [size]}) =>
                t('generic.forms.validation.errors.minFileSize', [formatFileSize(size)]),
            ),
            minLength: withMessage(regleRules.minLength, ({$value, $params: [minCount, options]}) => {
                const allowEqual = options?.allowEqual ?? true;
                if (Array.isArray($value)) {
                    if (allowEqual) {
                        return t('generic.forms.validation.errors.minLength.array.equal', [minCount]);
                    } else {
                        return t('generic.forms.validation.errors.minLength.array', [minCount]);
                    }
                } else if (allowEqual) {
                    return t('generic.forms.validation.errors.minLength.equal', [minCount]);
                } else {
                    return t('generic.forms.validation.errors.minLength', [minCount]);
                }
            }),
            minValue: withMessage(regleRules.minValue, ({$params: [minCount, options]}) =>
                (options?.allowEqual ?? true)
                    ? t('generic.forms.validation.errors.minValue.equal', [minCount])
                    : t('generic.forms.validation.errors.minValue', [minCount]),
            ),
            numeric: withMessage(regleRules.numeric, t('generic.forms.validation.errors.numeric')),
            oneOf: withMessage(regleRules.oneOf, ({$params: [values]}) => {
                const oneOfValues = Array.isArray(values) ? values : Object.values(values);
                return t('generic.forms.validation.errors.oneOf', [oneOfValues.join(', ')]);
            }),
            required: withMessage(regleRules.required, t('generic.forms.validation.errors.required')),
            startsWith: withMessage(regleRules.startsWith, ({$params: [prefix]}) => t('generic.forms.validation.errors.startsWith', [prefix])),
            url: withMessage(regleRules.url, t('generic.forms.validation.errors.url')),

            // ----
            // - Custom rules
            // ----
            bic,
            iban,
            minPrice,
            maxPrice,
            minYear,
            maxYear,
            maxImageDimensions,
            minImageDimensions,
        };
    },
    modifiers: {
        autoDirty: true,
    },
    shortcuts: {
        fields: {
            $isRequired: (field) => field.$rules.required?.$active ?? false,
        },
    },
});
